import React from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AdminFormRow from '../admin/AdminFormRow';
import { adminColors } from '../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminCheckboxSx,
  adminFormFieldSx,
  adminInlineControlLabelSx,
} from '../../styles/adminComponentStyles';

const lottoDialogFieldSx = adminCombineSx(adminFormFieldSx, {
  '& .MuiFormHelperText-root': {
    color: '#666666',
    fontSize: '14px',
    lineHeight: 1.3,
    margin: '4px 0 0',
  },
});

type LottoCredentialsDialogProps = {
  open: boolean;
  editMode: 'retailer_id' | 'credentials';
  username: string;
  password: string;
  lotteryRetailerId: string;
  saving: boolean;
  passwordRevealing: boolean;
  passwordVisible: boolean;
  onChangeEditMode: (value: 'retailer_id' | 'credentials') => void;
  onChangeUsername: (value: string) => void;
  onChangePassword: (value: string) => void;
  onChangeLotteryRetailerId: (value: string) => void;
  onRevealPassword: () => void;
  onCancel: () => void;
  onSubmit: () => void;
};

const LottoCredentialsDialog: React.FC<LottoCredentialsDialogProps> = ({
  open,
  editMode,
  username,
  password,
  lotteryRetailerId,
  saving,
  passwordRevealing,
  passwordVisible,
  onChangeEditMode,
  onChangeUsername,
  onChangePassword,
  onChangeLotteryRetailerId,
  onRevealPassword,
  onCancel,
  onSubmit,
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }) }}
  >
    <DialogTitle
      sx={adminCombineSx(adminDialogTitleSx, {
        backgroundColor: 'rgb(239, 246, 255)',
        color: adminColors.primary,
        fontWeight: 600,
      })}
    >
      Lotto Setup
    </DialogTitle>
    <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px' })}>
      <Stack spacing={1.5}>
        <AdminFormRow label="Username" required={editMode === 'credentials'}>
          <TextField
            fullWidth
            size="small"
            value={username}
            onChange={(e) => onChangeUsername(e.target.value)}
            disabled={saving || editMode !== 'credentials'}
            sx={lottoDialogFieldSx}
          />
        </AdminFormRow>
        <AdminFormRow label="Password" required={editMode === 'credentials'}>
          <Stack spacing={0}>
            <TextField
              type={passwordVisible ? 'text' : 'password'}
              fullWidth
              size="small"
              value={password}
              onChange={(e) => onChangePassword(e.target.value)}
              disabled={saving}
              InputProps={{
                readOnly: editMode !== 'credentials',
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={passwordVisible ? 'Hide password' : 'Show password'}>
                      <span>
                        <IconButton
                          aria-label={passwordVisible ? 'Hide lottery password' : 'Show lottery password'}
                          edge="end"
                          size="small"
                          onClick={onRevealPassword}
                          onMouseDown={(event) => event.preventDefault()}
                          disabled={saving || passwordRevealing}
                        >
                          {passwordRevealing ? (
                            <CircularProgress size={18} />
                          ) : passwordVisible ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={lottoDialogFieldSx}
            />
            {editMode !== 'credentials' ? (
              <Typography sx={{ color: '#808080', fontSize: '12px', fontStyle: 'italic', lineHeight: 1.3, mt: '4px' }}>
                Check Edit Credentials to update username and password.
              </Typography>
            ) : null}
          </Stack>
        </AdminFormRow>
        <AdminFormRow label="Lottery Retailer ID" required={editMode !== 'credentials'}>
          <Stack spacing={0}>
            <TextField
              fullWidth
              size="small"
              value={lotteryRetailerId}
              onChange={(e) => onChangeLotteryRetailerId(e.target.value)}
              disabled={saving || editMode === 'credentials'}
              sx={lottoDialogFieldSx}
            />
            {editMode === 'credentials' ? (
              <Typography sx={{ color: '#808080', fontSize: '12px', fontStyle: 'italic', lineHeight: 1.3, mt: '4px' }}>
                Uncheck Edit Credentials to update retailer ID.
              </Typography>
            ) : null}
          </Stack>
        </AdminFormRow>
      </Stack>
    </DialogContent>
    <DialogActions sx={adminCombineSx(adminDialogActionsSx, { justifyContent: 'space-between' })}>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={editMode === 'credentials'}
              onChange={(event) => onChangeEditMode(event.target.checked ? 'credentials' : 'retailer_id')}
              sx={adminCheckboxSx}
            />
          }
          label="Edit Credentials"
          sx={adminInlineControlLabelSx}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Button onClick={onCancel} disabled={saving} variant="outlined" size="small" sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onSubmit}
          disabled={saving}
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
        >
          Save
        </Button>
      </Stack>
    </DialogActions>
  </Dialog>
);

export default React.memo(LottoCredentialsDialog);
